/**
 * Eagerly prefetches `/api/games/:pk/players/:id` for a set of player ids
 * so clicking a player name/badge opens the modal with data already in
 * the React Query cache — no "Player {id}" flicker.
 *
 * Failure-aware tracking: `seen` is a `Map<key, status>` where status is
 * 'pending' | 'ok' | 'fail'. A failed prefetch (auth blip, network glitch)
 * is retried after 30s instead of permanently marked seen. The key includes
 * the user's uid so a sign-in / sign-out cycle starts fresh prefetches.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { playerStatsQueryOptions } from './players';

type Status = 'pending' | 'ok' | { kind: 'fail'; at: number };
const RETRY_BACKOFF_MS = 30_000;

export function usePrefetchPlayers(
  gamePk: number | undefined,
  playerIds: number[],
): void {
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();
  const seen = useRef<Map<string, Status>>(new Map());

  useEffect(() => {
    if (!user || !gamePk) return;
    const now = Date.now();
    for (const id of playerIds) {
      if (!Number.isFinite(id) || id <= 0) continue;
      const key = `${user.uid}:${gamePk}:${id}`;
      const prior = seen.current.get(key);
      // Skip if already pending or succeeded.
      if (prior === 'pending' || prior === 'ok') continue;
      // Retry failed prefetches once the backoff window elapses.
      if (
        prior &&
        typeof prior === 'object' &&
        prior.kind === 'fail' &&
        now - prior.at < RETRY_BACKOFF_MS
      ) {
        continue;
      }
      seen.current.set(key, 'pending');
      // fetchQuery, not prefetchQuery: prefetchQuery swallows errors and
      // always resolves, which would mark failed prefetches 'ok' and make
      // the 30s retry path unreachable. fetchQuery has the same cache/
      // staleTime semantics but rejects on failure.
      queryClient
        .fetchQuery(playerStatsQueryOptions(gamePk, id, getIdToken))
        .then(() => {
          seen.current.set(key, 'ok');
        })
        .catch(() => {
          seen.current.set(key, { kind: 'fail', at: Date.now() });
        });
    }
  }, [gamePk, playerIds, queryClient, getIdToken, user]);
}
